/**
 * Webinar Management API Service
 * Admin CRUD operations for webinars, recordings, and speakers
 */

import axios from 'axios';

import { config as productConfig } from '@/src/config';
const API_BASE = import.meta.env.MODE === 'production' ? '' : (productConfig.weaveinApi.baseUrl ?? '').trim();

// ============================================================================
// TYPES
// ============================================================================

export interface Webinar {
  id: number;
  event_id: string;
  title: string;
  description?: string;
  // API returns formatted date/time for public endpoints
  date?: string;  // e.g., "Jan 15, 2025"
  time?: string;  // e.g., "11:00 AM"
  // Admin endpoints use these raw fields
  event_date?: string;
  event_time?: string;
  timezone: string;
  duration_minutes: number;
  zoom_link?: string;
  zoom_webinar_id?: string;
  zoom_passcode?: string;
  status: 'draft' | 'published' | 'cancelled' | 'completed';
  is_featured: boolean;
  registration_count: number;
  max_attendees?: number;
  tags?: string[];
  recording_url?: string;
  recording_duration_minutes?: number;
  recording_thumbnail_url?: string;
  recording_published_at?: string;
  has_recording?: boolean;
  created_at?: string;
  created_by?: string;
}

export interface WebinarCreate {
  title: string;
  description?: string;
  event_date: string;  // YYYY-MM-DD
  event_time: string;  // HH:MM
  timezone?: string;
  duration_minutes?: number;
  zoom_webinar_link?: string;
  zoom_webinar_id?: string;
  zoom_passcode?: string;
  status?: 'draft' | 'published';
  is_featured?: boolean;
  max_attendees?: number;
  tags?: string[];
}

export interface WebinarUpdate {
  title?: string;
  description?: string;
  event_date?: string;
  event_time?: string;
  timezone?: string;
  duration_minutes?: number;
  zoom_webinar_link?: string;
  zoom_webinar_id?: string;
  zoom_passcode?: string;
  status?: 'draft' | 'published' | 'cancelled' | 'completed';
  is_featured?: boolean;
  max_attendees?: number;
  tags?: string[];
  recording_url?: string;
  recording_duration_minutes?: number;
  recording_thumbnail_url?: string;
}

export interface Speaker {
  id?: number;
  name: string;
  title?: string;
  organization?: string;
  bio?: string;
  photo_url?: string;
  linkedin_url?: string;
  display_order?: number;
}

export interface WebinarResource {
  id?: number;
  title: string;
  resource_type: 'pdf' | 'slide_deck' | 'worksheet' | 'link';
  file_url?: string;
  is_public?: boolean;
}

export interface WebinarDetails extends Webinar {
  speakers: Speaker[];
  resources: WebinarResource[];
}

export interface UpcomingWebinarsResponse {
  upcoming: Webinar[];
  recordings?: Webinar[];
}

export interface RecordingsResponse {
  recordings: Webinar[];
  count: number;
}

// ============================================================================
// HELPER
// ============================================================================

function getAuthHeader(): { headers: { Authorization: string } } {
  const token = localStorage.getItem('auth_token');
  return {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
}

// ============================================================================
// PUBLIC ENDPOINTS
// ============================================================================

/**
 * Get upcoming published webinars (public)
 */
export async function getUpcomingWebinars(includeRecordings = false): Promise<UpcomingWebinarsResponse> {
  const response = await axios.get<UpcomingWebinarsResponse>(
    `${API_BASE}/api/webinars/public/upcoming?include_recordings=${includeRecordings}`
  );
  return response.data;
}

/**
 * Get webinar recordings (public)
 */
export async function getWebinarRecordings(): Promise<RecordingsResponse> {
  const response = await axios.get<RecordingsResponse>(
    `${API_BASE}/api/webinars/public/recordings`
  );
  return response.data;
}

/**
 * Get webinar details by event_id (public)
 */
export async function getWebinarDetails(eventId: string): Promise<WebinarDetails> {
  const response = await axios.get<WebinarDetails>(
    `${API_BASE}/api/webinars/public/${eventId}`
  );
  return response.data;
}

// ============================================================================
// ADMIN ENDPOINTS
// ============================================================================

/**
 * List all webinars (admin)
 */
export async function listWebinars(statusFilter?: string): Promise<{ webinars: Webinar[]; count: number }> {
  const params = statusFilter ? `?status_filter=${statusFilter}` : '';
  const response = await axios.get(
    `${API_BASE}/api/webinars/admin/list${params}`,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Create a new webinar (admin)
 */
export async function createWebinar(data: WebinarCreate): Promise<{ success: boolean; id: number; event_id: string }> {
  const response = await axios.post(
    `${API_BASE}/api/webinars/admin/create`,
    data,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Update a webinar (admin)
 */
export async function updateWebinar(webinarId: number, data: WebinarUpdate): Promise<{ success: boolean; id: number; event_id: string }> {
  const response = await axios.put(
    `${API_BASE}/api/webinars/admin/${webinarId}`,
    data,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Delete a webinar (admin)
 */
export async function deleteWebinar(webinarId: number): Promise<{ success: boolean; message: string }> {
  const response = await axios.delete(
    `${API_BASE}/api/webinars/admin/${webinarId}`,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Add a speaker to a webinar (admin)
 */
export async function addSpeaker(webinarId: number, speaker: Speaker): Promise<{ success: boolean; speaker_id: number }> {
  const response = await axios.post(
    `${API_BASE}/api/webinars/admin/${webinarId}/speakers`,
    speaker,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Remove a speaker (admin)
 */
export async function removeSpeaker(speakerId: number): Promise<{ success: boolean }> {
  const response = await axios.delete(
    `${API_BASE}/api/webinars/admin/speakers/${speakerId}`,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Add a resource to a webinar (admin)
 */
export async function addResource(webinarId: number, resource: WebinarResource): Promise<{ success: boolean; resource_id: number }> {
  const response = await axios.post(
    `${API_BASE}/api/webinars/admin/${webinarId}/resources`,
    resource,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Remove a resource (admin)
 */
export async function removeResource(resourceId: number): Promise<{ success: boolean }> {
  const response = await axios.delete(
    `${API_BASE}/api/webinars/admin/resources/${resourceId}`,
    getAuthHeader()
  );
  return response.data;
}

/**
 * Publish recording for a webinar (admin helper)
 */
export async function publishRecording(
  webinarId: number,
  recordingUrl: string,
  durationMinutes?: number,
  thumbnailUrl?: string
): Promise<{ success: boolean }> {
  return updateWebinar(webinarId, {
    recording_url: recordingUrl,
    recording_duration_minutes: durationMinutes,
    recording_thumbnail_url: thumbnailUrl,
    status: 'completed'
  });
}
